import { useEffect } from 'react';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { useTheme } from 'foundations-theming/utils';
import { useMediaQuery } from '../../../utils/Styling';
import LessonOverviewModalContainer from '../containers/LessonOverviewModalContainer';
import TrackOverviewModalContainer from '../containers/TrackOverviewModalContainer';
import LiveEventOverviewModalContainer from '../containers/LiveEventOverviewModalContainer';
import UsageTracker from '../../../utils/UsageTracker';
import { HasAcademyOnboardingAccess } from '../../accounts/components/HasAcademyOnboardingAccess';
import { OVERVIEW_MAX_CONTENT_WIDTH } from './OverviewContent';
import { useOverviewNavigation } from '../overviewRouting';
import { useSetLastClickedRecommendationItemFromUrl } from '../../recommendations/recommendationsHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EVENT_MODAL_MAX_WIDTH = 936;
const OverviewModalLayout = ({
  entityId,
  language,
  source,
  type,
  children
}) => {
  const theme = useTheme();
  const {
    isMobile
  } = useMediaQuery();
  const {
    closeOverview
  } = useOverviewNavigation();
  const handleClose = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
      action: 'overview-modal-close',
      appearsIn: source
    }, type === 'TRACK' && {
      trackId: entityId
    }, type === 'LESSON' && {
      lessonId: entityId
    }, type === 'LIVE_EVENT' && {
      contentId: entityId
    }, {
      language
    }));
    closeOverview();
  };
  useEffect(() => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'overview-modal-view',
      contentId: entityId,
      contentType: type,
      appearsIn: source
    });
  }, [source, entityId, type]);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "overview-modal",
    dialogStyles: {
      blockSize: isMobile ? undefined : `calc(100% - ${theme.space['1300']})`,
      marginBlockStart: isMobile ? undefined : '1300',
      marginInline: 'auto',
      maxInlineSize: type === 'LIVE_EVENT' ? `${EVENT_MODAL_MAX_WIDTH}px` : `${OVERVIEW_MAX_CONTENT_WIDTH}px`
    },
    onBackdropClick: handleClose,
    onEsc: handleClose,
    use: "fullscreen",
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      color: "icon-core-on-fill-default",
      "data-test-id": "overview-close-button",
      detached: true,
      onClick: handleClose,
      size: "lg"
    }), /*#__PURE__*/_jsx(DialogBody, {
      overflow: "visible",
      padding: "0",
      children: children
    })]
  });
};
const LearningOverviewModal = ({
  entityId,
  language,
  source,
  type
}) => {
  useSetLastClickedRecommendationItemFromUrl({
    id: entityId,
    type
  });
  return /*#__PURE__*/_jsx(OverviewModalLayout, {
    entityId: entityId,
    language: language,
    source: source,
    type: type,
    children: type === 'TRACK' ? /*#__PURE__*/_jsx(TrackOverviewModalContainer, {
      isPlaylistOverview: false,
      language: language,
      source: source,
      trackId: entityId
    }) : /*#__PURE__*/_jsx(LessonOverviewModalContainer, {
      isPlaylistOverview: false,
      language: language,
      lessonId: entityId,
      source: source
    })
  });
};
const LiveEventOverviewModal = ({
  entityId,
  language,
  source
}) => {
  return /*#__PURE__*/_jsx(OverviewModalLayout, {
    entityId: entityId,
    language: language,
    source: source,
    type: "LIVE_EVENT",
    children: /*#__PURE__*/_jsx(HasAcademyOnboardingAccess, {
      children: /*#__PURE__*/_jsx(LiveEventOverviewModalContainer, {
        language: language,
        liveEventId: entityId
      })
    })
  });
};
export const OverviewModal = ({
  entityId,
  language,
  source,
  type
}) => {
  if (type === 'LIVE_EVENT') {
    return /*#__PURE__*/_jsx(LiveEventOverviewModal, {
      entityId: entityId,
      language: language,
      source: source
    });
  }
  return /*#__PURE__*/_jsx(LearningOverviewModal, {
    entityId: entityId,
    language: language,
    source: source,
    type: type
  });
};
export default OverviewModal;